import React, { useEffect, useState } from 'react';
import { getWorkspaceCounts } from '../lib/workspaceActions';

const formatCountLabel = (count, singular) =>
  `${count} ${count === 1 ? singular : `${singular}s`}`;

/**
 * Two-step delete confirmation for a workspace.
 * Controlled: open via isOpen; parent owns confirm/cancel.
 */
const DeleteWorkspaceModal = ({
  isOpen,
  workspace,
  isConfirmStep,
  onCancel,
  onConfirm,
  fallbackWorkspaceName,
  willCreateEmptyWorkspace = false,
}) => {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!isOpen || !workspace?.id) {
      setCounts(null);
      return undefined;
    }

    setCounts(null);
    getWorkspaceCounts(workspace.id)
      .then((result) => {
        if (!cancelled) setCounts(result);
      })
      .catch((error) => {
        console.error('Error loading workspace counts:', error);
        if (!cancelled) setCounts(null);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, workspace?.id]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl border-2 border-neutral-900 shadow-lg max-w-sm w-full mx-4">
        <div className="px-6 py-4 border-b-2 border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">Delete Workspace</h3>
        </div>

        <div className="px-6 py-4 space-y-2">
          {!isConfirmStep ? (
            <p className="text-neutral-600">
              Are you sure you want to delete &quot;{workspace?.name}&quot;?
            </p>
          ) : (
            <p className="text-cranberry-700 font-medium">
              {counts
                ? `This will permanently delete its ${formatCountLabel(counts.basketCount, 'basket')} and ${formatCountLabel(counts.topicCount, 'topic')}. This action cannot be undone.`
                : 'This will permanently delete all associated baskets and topics. This action cannot be undone.'}
              {fallbackWorkspaceName
                ? ` You'll be moved to ${fallbackWorkspaceName}.`
                : ''}
              {willCreateEmptyWorkspace
                ? ' A new empty workspace will be created for you.'
                : ''}
            </p>
          )}
          {counts && !isConfirmStep && (
            <p className="text-sm text-neutral-500">
              {formatCountLabel(counts.basketCount, 'basket')} · {formatCountLabel(counts.topicCount, 'topic')}
            </p>
          )}
        </div>

        <div className="px-6 py-4 bg-neutral-50 rounded-b-xl border-t-2 border-neutral-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-neutral-900 hover:text-cranberry-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`px-4 py-2 rounded-xl border-2 border-neutral-900 font-medium
              ${isConfirmStep
                ? 'bg-cranberry-700 text-white hover:bg-cranberry-600'
                : 'bg-neutral-100 text-neutral-900 hover:bg-neutral-200'
              } transition-colors`}
          >
            {isConfirmStep ? 'Confirm Delete' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteWorkspaceModal;
